/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderNavigationService, JobDetailsModel } from '@gh/builder-shared';
import { BuilderPermissionSet, JobVariationStatus } from '@gh/builder-shared-data';
import { rendererIconByName, truthyCellRenderer } from '@gh/common-internal-shared';
import { AppRouter, DateTimeTypes, getRouteInitData, NumberTypes, UserContext } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { translationTuple } from '@gh/core-mls';
import {
  buildDateTimeColumn,
  buildNumberColumn,
  GridDefinitionFactory,
  GridRowEvent,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { assign } from 'lodash';
import { JobVariation, JobVariationResponse } from '../../../resources/job-details-tab/job-details-tab-model';
import { JobCoListItem } from '../../../resources/job.model';

@Component({
  selector: 'gh-job-details-variation-list',
  template: require('./job-details-variation-list-tab.component.html'),
})
export class JobDetailsVariationListTabComponent implements OnInit {
  gridDefinition: LayoutBasedGridDefinition;
  totalGd: LayoutBasedGridDefinition;
  variationData: JobVariationResponse;
  jobId: ID;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private gridDefinitionFactory: GridDefinitionFactory,
              private navigationService: BuilderNavigationService,
              private model: JobDetailsModel,
              private userContext: UserContext) {
  }

  ngOnInit(): void {
    const { gridLayout, totalGridLayout, variationData, jobId } = getRouteInitData(this.route);

    assign(this, { variationData, jobId });

    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        dateCreated: buildDateTimeColumn(DateTimeTypes.DateTime),
        timeExtensionDays: buildNumberColumn(NumberTypes.Days),
        amount: buildNumberColumn(NumberTypes.Price),
        amountWithGst: buildNumberColumn(NumberTypes.Price),
        status: {
          cellClass: ({ data }: { data: JobCoListItem }) => ({
            [JobVariationStatus.Quote]: 'cell--unimportant',
            [JobVariationStatus.Approved]: 'cell--positive',
            [JobVariationStatus.Reversed]: 'cell--important',
            [JobVariationStatus.Rejected]: 'cell--negative',
          }[data.status]),
        },
        isDrawn: {
          cellRenderer: truthyCellRenderer(rendererIconByName('done')),
        },
      },
      {
        getRowNodeId: (variation: JobVariation) => variation.id,
      });
    this.gridDefinition.bind(gridLayout);

    this.totalGd = this.gridDefinitionFactory.buildLayoutBased(
      {
        totalAmount: {
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Price),
        },
        totalAmountWithGst: {
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Price),
        },
        status: {
          suppressSizeToFit: true,
          cellClass: ({ data }: { data: JobCoListItem }) => ({
            [JobVariationStatus.Quote]: 'cell--unimportant',
            [JobVariationStatus.Approved]: 'cell--positive',
            [JobVariationStatus.Reversed]: 'cell--important',
            [JobVariationStatus.Rejected]: 'cell--negative',
          }[data.status]),
        },
        timeExtensionTotal: { suppressSizeToFit: true },
      },
      {
        getRowNodeId: (data: JobVariation) => data.status,
        defaultColDef: {
          sortable: false,
        },
      });
    this.totalGd.bind(totalGridLayout);
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.jobId,
      entityType: 'JOB',
      label: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
    });
  }

  onRowClicked($event: GridRowEvent<JobVariation>): void {
    this.onEdit($event.data);
  }

  onEdit(variation: JobVariation): void {
    if (!this.userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobChangeOrders.EDIT_VIEW)) {
      return;
    }

    this.router.navigateToChild(['/job-admin/jobs', this.jobId, 'change-orders', variation.id], {
      parentTitle: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
    });
  }
}
